import { useState } from "react"

import {
  usePostAuthCodeConfirm,
  usePostAuthCodeRequest,
  type PostAuthCodeConfirmMutationResult,
} from "@boundless/bl-api-client/allauth/auth"
import { Lock } from "lucide-react"
import mixpanel from "mixpanel-browser"

import { CodeEntryForm } from "@/components/account/CodeEntryForm"
import {
  StickyFormActions,
  useIsInAccountFormColumn,
} from "@/components/account/layout/StickyFormActions"
import { Button, LoadingButton } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  type AllauthErrorType,
  getCodeRequestRateLimitedMessage,
  isCodeRequestRateLimitedResponse,
  isLoginCodePendingResponse,
} from "@/lib/account/utils"
import { getAuthClient } from "@/lib/clients"
import { RESEND_SECONDS } from "@/lib/constants"
import { store } from "@/lib/store"
import { isToastableError, redirectTo } from "@/lib/utils"

type LoginByCodeFormProps = {
  onSuccess?: (response: PostAuthCodeConfirmMutationResult) => void
  // Omit to hide the "Use a password instead" link (e.g. the embed flow,
  // which is code-only).
  onUsePassword?: () => void
  email?: string | null
  // Lock the email field (read-only, no editing). Used when the email is
  // already known, e.g. a returning embed borrower confirming it's them.
  lockEmail?: boolean
}

export function LoginByCodeForm({
  onSuccess,
  onUsePassword,
  email: initialEmail,
  lockEmail = false,
}: LoginByCodeFormProps) {
  const client = getAuthClient()
  const [email, setEmail] = useState(initialEmail ?? "")
  const isInAccountLayout = useIsInAccountFormColumn()
  const [step, setStep] = useState<"email" | "code">("email")
  const [requestError, setRequestError] = useState<string | null>(null)
  const [codeError, setCodeError] = useState<string | null>(null)

  const { isPending: isRequesting, mutate: requestCode } =
    usePostAuthCodeRequest({
      mutation: {
        meta: {
          // The inline "please wait" message already covers the throttled case.
          shouldDisplayErrorToast: (error) =>
            isToastableError(error) &&
            !isCodeRequestRateLimitedResponse(error as AllauthErrorType),
        },
        // A successful request returns 401 with the login_by_code flow
        // pending, so the "code sent" case arrives here, not in onSuccess.
        onSuccess: () => setStep("code"),
        onError: (error) => {
          if (isLoginCodePendingResponse(error)) {
            setStep("code")
            return
          }
          if (isCodeRequestRateLimitedResponse(error)) {
            setRequestError(getCodeRequestRateLimitedMessage(RESEND_SECONDS))
            return
          }
          setRequestError(
            "We couldn't send a code right now. Please try again shortly.",
          )
        },
      },
    })

  const { isPending: isConfirming, mutate: confirmCode } =
    usePostAuthCodeConfirm({
      mutation: {
        onSuccess: (response) => {
          if (response.data.user.redirect) {
            redirectTo(response.data.user.redirect)
            return
          }

          mixpanel.identify(response.data.user.id)
          store.setState({
            isAuthenticated: response.meta.is_authenticated,
            userRole: response.data.user.role,
          })

          onSuccess?.(response)
        },
        onError: () =>
          setCodeError("That code is invalid or has expired. Try again."),
      },
    })

  function sendCode() {
    setRequestError(null)
    setCodeError(null)
    requestCode({ client, data: { email } })
  }

  if (step === "code") {
    return (
      <CodeEntryForm
        email={email}
        isSubmitting={isConfirming}
        error={codeError}
        submitLabel="Verify & sign in"
        onSubmit={(code) => {
          setCodeError(null)
          confirmCode({ client, data: { code } })
        }}
        onResend={sendCode}
      />
    )
  }

  return (
    <form
      className="flex flex-1 flex-col"
      onSubmit={(event) => {
        event.preventDefault()
        sendCode()
      }}
    >
      <div className="flex flex-1 flex-col gap-4">
        <Input
          name="email"
          type="email"
          placeholder="Email"
          autoComplete="email"
          autoFocus
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={isRequesting || lockEmail}
          readOnly={lockEmail}
        />

        {/* Mobile has no proof panel alongside to explain the code-first
            sign-in, so the design spells it out under the field. The embed
            login dialog already says this above the form, so it is scoped to
            the account layout. */}
        {isInAccountLayout && (
          <div className="flex items-start gap-2.5 lg:hidden">
            <Lock
              className="text-primary mt-0.5 size-4 shrink-0"
              strokeWidth={1.7}
            />
            <p className="text-[12.5px] leading-[1.5] text-neutral-500">
              No password needed. We email a six-digit code that expires in 10
              minutes.
            </p>
          </div>
        )}

        {requestError && (
          <p className="text-destructive text-center text-sm font-medium">
            {requestError}
          </p>
        )}

        <StickyFormActions>
          {isRequesting ? (
            <LoadingButton>Email me a sign-in code</LoadingButton>
          ) : (
            <Button type="submit">Email me a sign-in code</Button>
          )}

          {onUsePassword && (
            <div className="text-center">
              <Button
                variant="link"
                type="button"
                className="p-0 text-neutral-600 hover:text-neutral-800"
                onClick={onUsePassword}
              >
                Use a password instead
              </Button>
            </div>
          )}
        </StickyFormActions>
      </div>
    </form>
  )
}
